import React from "react";

/**
 * Silver readiness gauge — circular SVG arc, 0..100 scale.
 * Used in the Cockpit (/platform) to show how far each VNG step is along.
 *
 *   value      → 0..100
 *   label      → text in the centre (kept very short)
 *   size       → outer pixel size
 *
 * Visual: thin silver hairline track + brighter silver progress arc + tiny tick marks at 0/25/50/75/100.
 */
const polar = (cx, cy, r, deg) => {
  const rad = ((deg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
};

const ReadinessGauge = ({
  value = 0,
  label = "",
  caption = "",
  size = 180,
  testid,
}) => {
  const v = Math.max(0, Math.min(100, value));
  const cx = 100;
  const cy = 100;
  const r = 80;
  const startDeg = -130;
  const endDeg = 130;
  const sweep = endDeg - startDeg; // 260°
  const arcEnd = startDeg + (sweep * v) / 100;

  const [sx, sy] = polar(cx, cy, r, startDeg);
  const [ex, ey] = polar(cx, cy, r, endDeg);
  const [px, py] = polar(cx, cy, r, arcEnd);

  const largeArc = endDeg - startDeg > 180 ? 1 : 0;
  const largeArcFill = arcEnd - startDeg > 180 ? 1 : 0;

  return (
    <div
      data-testid={testid}
      className="flex flex-col items-center mx-auto w-full"
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 200 200"
        fill="none"
        aria-label={`${label} ${v}%`}
        style={{ display: "block" }}
      >
        {/* Track */}
        <path
          d={`M ${sx} ${sy} A ${r} ${r} 0 ${largeArc} 1 ${ex} ${ey}`}
          stroke="#C0C5CE"
          strokeOpacity="0.18"
          strokeWidth="1.5"
          fill="none"
        />
        {/* Progress */}
        {v > 0 && (
          <path
            d={`M ${sx} ${sy} A ${r} ${r} 0 ${largeArcFill} 1 ${px} ${py}`}
            stroke="#C0C5CE"
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
            style={{
              filter: "drop-shadow(0 0 6px rgba(192,197,206,0.45))",
            }}
          />
        )}
        {/* Centre value */}
        <text
          x="100"
          y="98"
          textAnchor="middle"
          fontFamily="Fraunces, serif"
          fontSize="34"
          fontWeight="300"
          fill="#FFFFFF"
        >
          {Math.round(v)}
        </text>
        <text
          x="100"
          y="120"
          textAnchor="middle"
          fontFamily="Outfit, sans-serif"
          fontSize="9"
          letterSpacing="3"
          fill="#9AA4B2"
        >
          {caption || "READINESS"}
        </text>
      </svg>
      <div
        className="mt-4 text-center text-[10px] tracking-[0.22em] uppercase text-[#D4D8DF] leading-snug"
        style={{ fontFamily: "Outfit, sans-serif", fontWeight: 400 }}
      >
        {label}
      </div>
    </div>
  );
};

export default ReadinessGauge;
